import {InputDate} from '@/components/commons/input-date/InputDate'
import {ProjectFieldsSchema} from '@/features/projects/formSchemas'
import {useGetHolidays} from '@/features/projects/queries/useGetHolidays'
import {getNextWorkingDay} from '@/features/projects/utils'
import {useTranslation} from '@/translations/i18n'
import dayjs from 'dayjs'
import {useController, useFormContext, useWatch} from 'react-hook-form'
import {useCallback} from 'react'
import {z} from 'zod'

const ProjectFormEndDateFieldSchema = ProjectFieldsSchema.pick({
    startDate: true,
    endDate: true,
    expirationDate: true,
    durationDays: true,
    referringProject: true
})
type ProjectFormEndDateFieldSchema = z.infer<typeof ProjectFormEndDateFieldSchema>

export const ProjectFormEndDateField = () => {
    const {t} = useTranslation()
    const {control, setError, clearErrors} = useFormContext<ProjectFormEndDateFieldSchema>()
    const {field, fieldState} = useController({control, name: 'endDate'})
    const {field: startDateField} = useController({control, name: 'startDate'})
    const {field: expirationDateField} = useController({control, name: 'expirationDate'})
    const durationDays = useWatch({control, name: 'durationDays'})
    const referringProject = useWatch({control, name: 'referringProject'})
    const {data: holidays} = useGetHolidays({
        urlParams: {year: dayjs(startDateField.value).year()},
        options: {
            enabled: dayjs(startDateField.value).isValid()
        }
    })

    const handleChangeEndDateField = (date: Date | null) => {
        if (date) clearErrors('endDate')
        field.onChange(date)
        const newStartDate = date ? getNextWorkingDay(date, holidays || []) : null
        startDateField.onChange(newStartDate)
        expirationDateField.onChange(
            newStartDate
                ? dayjs(newStartDate)
                      .add(durationDays || 0, 'day')
                      .toDate()
                : null
        )
    }

    const handleDateOutOfPeriod = useCallback(
        (date: Date) => {
            const expiration = referringProject?.expirationDate
            if (expiration && dayjs(date).isAfter(dayjs(expiration), 'day')) {
                setError('endDate', {
                    type: 'manual',
                    message: t('projects:early_closing_date_after_expiration')
                })
            }
        },
        [referringProject?.expirationDate, setError, t]
    )

    return (
        <InputDate
            label={t('projects:end_date')}
            value={field.value}
            onChange={handleChangeEndDateField}
            onDateOutOfPeriod={handleDateOutOfPeriod}
            placeholder="GG/MM/AAAA"
            defaultMonth={
                referringProject?.startDate ? getNextWorkingDay(new Date(referringProject.startDate)) : undefined
            }
            disableBeforeDate={referringProject?.startDate ? dayjs(referringProject.startDate).toDate() : undefined}
            disableAfterDate={
                referringProject?.expirationDate ? dayjs(referringProject.expirationDate).toDate() : undefined
            }
            errorMessage={fieldState.error?.message}
            fullWidth
        />
    )
}
